/**
 * Transferir — mesclar um cadastro noutro.
 *
 * É o que se faz quando a mesma coisa foi cadastrada duas vezes: "ECM" e
 * "ECM " com espaço, dois circuitos com o mesmo nome, duas cores iguais. A
 * transferência move **todos os pinouts** da origem para o destino e deixa a
 * origem vazia, pronta para ser excluída.
 *
 * **É irreversível e mexe em muita linha de uma vez**, então a confirmação não
 * é um "tem certeza?": ela diz quantos pinouts vão mudar de dono, mostra os dois
 * nomes lado a lado, e só libera o botão depois que a pessoa escolheu o destino.
 * Trocar origem e destino por engano é o erro caro aqui — dá para desfazer
 * transferindo de volta, mas só se alguém perceber.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { ItemDeCadastro } from "@/api/cadastros"
import type { ItemDeCatalogo } from "@/api/catalogos"
import { CampoDeBusca, idDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"

export function Transferir({
  rota, projetoId, origem, candidatos, aoFechar, aoTransferir,
}: {
  /** `api/componentes`, `api/circuitos`… — a mesma rota do cadastro. */
  rota: string
  projetoId: number
  /** O registro que vai ser esvaziado. */
  origem: ItemDeCadastro
  /** De onde sai a lista de destinos possíveis. */
  candidatos: ItemDeCatalogo[]
  aoFechar: () => void
  aoTransferir: () => void
}) {
  const qc = useQueryClient()
  const [destino, setDestino] = useState<ValorBusca>(VAZIO)
  const destinoId = idDe(destino)

  const transferir = useMutation({
    mutationFn: async () => {
      await api.post(
        `${rota}/transferir`,
        { origemId: origem.id, destinoId },
        // O projeto vai na consulta, e é assim que a trava o enxerga.
        noProjeto(projetoId, { params: { projetoId } }),
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cadastro", rota] })
      qc.invalidateQueries({ queryKey: ["catalogo"] })
      qc.invalidateQueries({ queryKey: ["pinouts", projetoId] })
      aoTransferir()
    },
  })

  // Nunca para si mesmo: transferir um registro para ele próprio ou apagaria
  // tudo ou não faria nada, e nenhum dos dois é o que a pessoa quis.
  const possiveis = candidatos.filter((c) => c.id !== origem.id)

  return (
    <div style={{
      padding: "11px 12px", borderRadius: 9, display: "flex", flexDirection: "column", gap: 9,
      background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
    }}>
      <b style={{ fontSize: 12.5 }}>Transferir para outro registro</b>

      <span style={{ fontSize: 11.5, lineHeight: 1.55 }}>
        Todos os{" "}
        <b className="num">{origem.qtd}</b>{" "}
        {origem.qtd === 1 ? "pinout" : "pinouts"} de{" "}
        <b>{String(origem.descricao || origem.id)}</b>{" "}
        passam para o destino escolhido, e este registro fica vazio.
      </span>

      <CampoDeBusca
        rotulo="Destino"
        valor={destino}
        aoMudar={setDestino}
        fonte={{ modo: "memoria", itens: possiveis, carregando: false }}
        permiteCriar={false}
        dica="para onde os pinouts vão"
        id="transferir-destino"
      />

      <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <button
          onClick={() => transferir.mutate()}
          disabled={destinoId <= 0 || transferir.isPending}
          style={{
            height: 30, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            background: destinoId > 0 ? "var(--acao)" : "var(--surface)",
            color: destinoId > 0 ? "var(--acao-ink)" : "var(--txt3)",
            border: destinoId > 0 ? undefined : "1px solid var(--line2)",
            opacity: transferir.isPending ? 0.6 : 1,
          }}
        >
          {transferir.isPending ? "Transferindo…" : "Transferir e esvaziar"}
        </button>
        <button onClick={aoFechar} style={{ height: 30, padding: "0 10px", fontSize: 12, color: "var(--txt2)" }}>
          Cancelar
        </button>
      </span>

      {origem.qtd === 0 && (
        <span style={{ fontSize: 11, opacity: 0.85 }}>
          Este registro não tem pinout nenhum — transferir não vai mudar nada. Talvez você queira
          apenas excluí-lo.
        </span>
      )}

      {transferir.error != null && (
        <span style={{ fontSize: 11.5, color: "var(--erro-on-soft)" }}>
          {transferir.error instanceof ErroDeTrava || transferir.error instanceof ErroDaApi
            || transferir.error instanceof Error
            ? transferir.error.message
            : "Não foi possível transferir."}
        </span>
      )}
    </div>
  )
}
